import { useState, useEffect } from "react";
import { sanitizeAndTrim, htmlScriptRegex } from "../constants/utils";

const useSafeInput = ({ value = "", limit = 1000, onChange, showToast }) => {
  const [localValue, setLocalValue] = useState(value || "");
  const [count, setCount] = useState(value?.length || 0);
  const [history, setHistory] = useState([value || ""]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // sync with parent value (avoid unnecessary re-renders)
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value || "");
      setCount(value?.length || 0);
    }
  }, [value]);

  //  BLOCK < >
  const handleKeyDown = (e) => {
    if (e.key.length === 1 && htmlScriptRegex.test(e.key)) {
      e.preventDefault();
      showToast(
        "Greater than (>) and less than (<) symbols are not allowed.",
        "warning",
        true
      );
    }
    // for undo with ctrl+z
    if ((e.ctrlKey && e.key === "z") || (e.metaKey && e.key === "z")) {
      e.preventDefault();
      if (historyIndex > 0) {
        const prevVal = history[historyIndex - 1];
        if (prevVal !== undefined) {
          setHistoryIndex((i) => i - 1);
          setLocalValue(prevVal);
          setCount(prevVal.length);
          onChange({ target: { value: prevVal } });
        }
      }
    }
    // for redo with ctrl + Shift + z
    else if ((e.ctrlKey && e.shiftKey && e.key === "Z") || (e.metaKey && e.shiftKey && e.key === "Z")) {
      e.preventDefault();
      if (historyIndex < history.length - 1) {
        const nextVal = history[historyIndex + 1];
        if (nextVal !== undefined) {
          setHistoryIndex((i) => i + 1);
          setLocalValue(nextVal);
          setCount(nextVal.length);
          onChange({ target: { value: nextVal } });
        }
      }
    }
  };

  //  HANDLE PASTE
  const handlePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData.getData("text");
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const combined =
      localValue.slice(0, start) + pasted + localValue.slice(end);
    const result = sanitizeAndTrim(combined, limit);

    if (result.blocked) {
      showToast(
        "Greater than (>) and less than (<) symbols are not allowed.",
        "warning",
        true
      );
      return;
    }

    updateValue(result.value);

    setTimeout(() => {
      const cursorPos = start + pasted.length;
      input.selectionStart = input.selectionEnd = cursorPos;
    }, 0);

    if (result.trimmed) {
      showToast(`Maximum ${limit} characters allowed.`, "warning", true);
    }
  };

  //  HANDLE CHANGE
  const handleChange = (e) => {
    const input = e.target;
    const val = e.target.value;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const result = sanitizeAndTrim(val, limit);

    if (result.blocked) {
      showToast(
        "Greater than (>) and less than (<) symbols are not allowed.",
        "warning",
        true
      );
      return;
    }

    updateValue(result.value);
    setTimeout(() => {
      input.selectionStart = start;
      input.selectionEnd = end;
    }, 0);

    if (result.trimmed) {
      showToast(`Maximum ${limit} characters allowed.`, "warning", true);
    }
  };

  //  SINGLE SOURCE UPDATE FUNCTION (important)
  const updateValue = (val) => {
    setLocalValue(val);
    setCount(val.length);
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      if (newHistory[newHistory.length - 1] !== val) {
        newHistory.push(val);
        setHistoryIndex(newHistory.length - 1);
      }
      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
    setHistoryIndex((prev) => prev + 1);
    onChange({ target: { value: val } });
  };

  return {
    value: localValue,
    count,
    setValue: updateValue, // reuse same logic
    bind: {
      value: localValue,
      onKeyDown: handleKeyDown,
      onPaste: handlePaste,
      onChange: handleChange,
    },
  };
};

export default useSafeInput;
